import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { sendAdminBroadcast, listenToBroadcasts } from '../services/firebase';
import { useLang } from '../lang/LangContext';

const PASSENGER_PRESETS = [
  { title: '🎉 Special Auto Discount in Pileru!', body: 'Book your auto ride today and get flat ₹10 discount.', target: 'passengers', lang: 'en' },
  { title: '🛺 Fast & Safe Rides in Pileru', body: 'Local autos at your doorstep in under 2 minutes. Tap to book now!', target: 'passengers', lang: 'en' },
  { title: '🌧️ వర్షం పడుతోంది - ఆటోలు సిద్ధంగా ఉన్నాయి', body: 'మీ ఇంటి వద్దకే ఆటో వస్తుంది. ఇప్పుడే బుక్ చేసుకోండి.', target: 'passengers', lang: 'te' },
  { title: '🎊 Festive Offer', body: 'Special festival ride offers active across all areas in Pileru.', target: 'passengers', lang: 'en' },
];

const DRIVER_PRESETS = [
  { title: '🌧️ Heavy Rain Alert', body: 'Piler market lo varsham padutondi. High ride demand undi!', target: 'drivers', lang: 'te' },
  { title: '🚌 Bus Stand Demand', body: 'Piler Bus Stand daggara ride reqs ekkuvaga unnayi! Please go online.', target: 'drivers', lang: 'te' },
  { title: '🚆 Railway Station Demand', body: 'Railway station daggara 10+ passengers waiting for rides!', target: 'drivers', lang: 'te' },
  { title: '⚙️ System Update', body: 'App system maintenance completed successfully.', target: 'all_drivers', lang: 'en' },
];

export default function Broadcast({ drivers = [], users = [], isSuperAdmin = false }) {
  const { t } = useLang();
  const [target, setTarget] = useState('passengers');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(false);
  const [broadcastHistory, setBroadcastHistory] = useState([]);

  const onlineCaptains = drivers.filter((d) => d.isOnline && d.isApproved && !d.isBlocked).length;
  const totalCaptains = drivers.length;
  const totalPassengers = users.length;

  useEffect(() => {
    const unsub = listenToBroadcasts((list) => {
      setBroadcastHistory(list);
    });
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!isSuperAdmin) {
      toast.error(t('Sending broadcasts requires Super Admin privileges.'));
      return;
    }
    if (!title.trim() || !body.trim()) {
      toast.error(t('Title and message body are required.'));
      return;
    }
    setLoading(true);
    try {
      await sendAdminBroadcast({ target, title, body, language });
      toast.success(t('📢 1-Click Broadcast dispatched to devices!'));
      setTitle('');
      setBody('');
    } catch (err) {
      console.error(err);
      toast.error(t('Failed to send broadcast.'));
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (preset) => {
    setTitle(preset.title);
    setBody(preset.body);
    setTarget(preset.target);
    setLanguage(preset.lang);
  };

  const getRecipientEstimate = () => {
    if (target === 'passengers') return `${totalPassengers} Registered Passengers`;
    if (target === 'drivers') return `${onlineCaptains} Online Captains`;
    if (target === 'all_drivers') return `${totalCaptains} Total Captains`;
    return `${totalPassengers + totalCaptains} Users (All)`;
  };

  return (
    <>
      <div className="stat-grid">
        <div className="stat-card orange">
          <div className="stat-label">{t('REGISTERED PASSENGERS')}</div>
          <div className="stat-value orange">{totalPassengers}</div>
          <div className="stat-sub">Passenger app users with push alerts</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">{t('ONLINE CAPTAINS')}</div>
          <div className="stat-value green">{onlineCaptains}</div>
          <div className="stat-sub">Active in Pileru right now</div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">{t('TOTAL CAPTAINS')}</div>
          <div className="stat-value blue">{totalCaptains}</div>
          <div className="stat-sub">Registered fleet in Pileru</div>
        </div>
      </div>

      {!isSuperAdmin && (
        <div className="card alert-card" style={{ marginBottom: '16px', border: '1px solid #f59e0b', background: 'rgba(245, 158, 11, 0.08)' }}>
          <strong style={{ color: '#f59e0b' }}>🔍 {t('Reviewer Access (Read-Only)')}</strong>
          <p className="muted" style={{ fontSize: 13, marginTop: 4, marginBottom: 0 }}>
            {t('You have read-only visibility into broadcasts and active fleet distribution. Sending push broadcasts to passengers and Annas requires Super Admin privileges.')}
          </p>
        </div>
      )}

      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{t('📢 1-Click Broadcast Push Notification Tool')}</span>
          <span className="badge badge-success" style={{ fontSize: '12px', padding: '4px 10px' }}>
            Direct FCM Cloud Multicast
          </span>
        </div>

        {/* 1-Click Presets */}
        <div className="field-group" style={{ marginBottom: '20px' }}>
          <label className="field-label">{t('⚡ 1-CLICK PRESETS FOR PASSENGERS')}</label>
          <div className="preset-pills-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {PASSENGER_PRESETS.map((p, idx) => (
              <button
                key={`p-${idx}`}
                type="button"
                className={`preset-pill ${title === p.title ? 'active' : ''}`}
                onClick={() => applyPreset(p)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  border: '1px solid #e2e8f0',
                  background: title === p.title ? '#00a86b' : '#f8fafc',
                  color: title === p.title ? '#ffffff' : '#334155',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                }}
              >
                {p.title}
              </button>
            ))}
          </div>
        </div>

        <div className="field-group" style={{ marginBottom: '24px' }}>
              <label className="field-label">{t('🛺 1-CLICK PRESETS FOR CAPTAINS')}</label>
          <div className="preset-pills-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {DRIVER_PRESETS.map((p, idx) => (
              <button
                key={`d-${idx}`}
                type="button"
                className={`preset-pill ${title === p.title ? 'active' : ''}`}
                onClick={() => applyPreset(p)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  border: '1px solid #e2e8f0',
                  background: title === p.title ? '#00a86b' : '#f8fafc',
                  color: title === p.title ? '#ffffff' : '#334155',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                }}
              >
                {p.title}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 0.9fr)', gap: '24px' }}>
          <form onSubmit={handleSend}>
            <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div className="field-group">
                <label className="field-label">{t('👥 TARGET AUDIENCE')}</label>
                <select
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                >
                  <option value="passengers">All Passengers ({totalPassengers})</option>
                  <option value="drivers">Online Captains Only ({onlineCaptains})</option>
                  <option value="all_drivers">All Captains ({totalCaptains})</option>
                  <option value="all">Everyone ({totalPassengers + totalCaptains})</option>
                </select>
              </div>
              <div className="field-group">
                <label className="field-label">{t('🌐 NOTIFICATION LANGUAGE')}</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                >
                  <option value="en">English</option>
                  <option value="te">Telugu (తెలుగు)</option>
                </select>
              </div>
            </div>

            <div className="field-group" style={{ marginBottom: '16px' }}>
                <label className="field-label">{t('🔔 NOTIFICATION TITLE')}</label>
              <input
                type="text"
                placeholder="e.g. 🎉 Special Auto Discount in Pileru!"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                required
              />
            </div>

            <div className="field-group" style={{ marginBottom: '20px' }}>
              <label className="field-label">{t('📝 MESSAGE BODY')}</label>
              <textarea
                rows={4}
                placeholder="Type push notification text here..."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', resize: 'vertical' }}
                required
              />
            </div>

            <button
              type="submit"
              className="btn-primary-large"
              disabled={!isSuperAdmin || loading}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '10px',
                background: !isSuperAdmin ? '#64748b' : '#00A86B',
                color: '#ffffff',
                border: 'none',
                fontWeight: '700',
                fontSize: '15px',
                cursor: (!isSuperAdmin || loading) ? 'not-allowed' : 'pointer',
                boxShadow: !isSuperAdmin ? 'none' : '0 4px 14px rgba(0, 168, 107, 0.3)',
              }}
            >
              {!isSuperAdmin ? '🔒 Broadcast Restricted (Super Admin Only)' : loading ? 'Dispatched to Cloud FCM...' : `🚀 Send Broadcast to ${getRecipientEstimate()}`}
            </button>
          </form>

          {/* Live Mobile Notification Preview */}
          <div>
            <label className="field-label" style={{ marginBottom: '8px', display: 'block' }}>📱 LIVE DEVICE PREVIEW</label>
            <div
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                background: '#f8fafc',
                padding: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
              }}
            >
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  padding: '12px 14px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  border: '1px solid #edf2f7',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '5px',
                      background: '#00A86B',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '11px',
                      fontWeight: '800',
                    }}
                  >
                    M
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>MyTownly Rides</span>
                  <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: 'auto' }}>Now</span>
                </div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', marginBottom: '2px' }}>
                  {title || 'Notification Title'}
                </div>
                <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.4' }}>
                  {body || 'Notification text will appear here on passengers and captains mobile lock screens.'}
                </div>
              </div>
              <div style={{ marginTop: '12px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                Delivering via high-priority Android & Web push notification channel.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Broadcast History Table */}
      <div className="card">
        <div className="card-title">📜 Broadcast Dispatch History</div>
        {broadcastHistory.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
            No broadcasts sent yet. Use the tool above to send your first announcement!
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                  <th style={{ padding: '10px' }}>TITLE & MESSAGE</th>
                  <th style={{ padding: '10px' }}>TARGET</th>
                  <th style={{ padding: '10px' }}>DELIVERY STATUS</th>
                  <th style={{ padding: '10px' }}>DATE & TIME</th>
                </tr>
              </thead>
              <tbody>
                {broadcastHistory.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 10px' }}>
                      <div style={{ fontWeight: '700', color: '#0f172a' }}>{item.title}</div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{item.body}</div>
                    </td>
                    <td style={{ padding: '12px 10px' }}>
                      <span style={{ textTransform: 'capitalize', fontWeight: '600', color: '#334155' }}>
                        {item.target === 'passengers' ? '👥 Passengers' : item.target === 'drivers' ? '🛺 Online Captains' : '🌐 All Fleet'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 10px' }}>
                      {item.status === 'sent' ? (
                        <span style={{ color: '#00A86B', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          ✓ Sent ({item.successCount || item.recipientCount || 0} devices)
                        </span>
                      ) : item.status === 'failed' ? (
                        <span style={{ color: '#ef4444', fontWeight: '700' }}>Failed</span>
                      ) : (
                        <span style={{ color: '#f59e0b', fontWeight: '600' }}>In flight…</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 10px', color: '#94a3b8', fontSize: '12px' }}>
                      {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString('en-IN') : 'Just now'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
